import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { BookOutlined, DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import { getAssetTypeName } from '@/shared/api/openmetadata';
import { COLOR } from '@/shared/design-tokens/design-tokens';
import { useAuthorization } from '@/shared/hooks';
import { getAssetIconStyle, getAssetIconWrapperStyle } from '@/shared/lib/asset';
import { EMPTY_MESSAGES, NOTIFICATION_MESSAGES, TREE_DEPTH } from '@/shared/lib/constants';
import { calculateFqnDepth } from '@/shared/lib/format/fqn';
import { Button } from '@/shared/ui/Button';
import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { SuspenseFallback } from '@/shared/ui/SuspenseFallback';

import { useDeleteGlossaryCategory, useDeleteGlossaryTerm } from '@/features/glossary/model';
import { useGlossaryTermCategoryInfo } from '@/features/glossary/model/useGlossaryTermCategoryInfo';
import { useGlossaryTermDetail } from '@/features/glossary/model/useGlossaryTermDetail';
import { GlossaryCategoryFormModal } from '@/features/glossary/ui/GlossaryCategoryFormModal';
import { GlossaryMetadataSidebar } from '@/features/glossary/ui/GlossaryMetadataSidebar';
import { GlossaryTable } from '@/features/glossary/ui/GlossaryTable';
import { GlossaryTermFormModal } from '@/features/glossary/ui/GlossaryTermFormModal';

import { GlossaryTermContent, GlossaryTermSummaryCard } from '@/widgets/glossary';

import styles from './GlossaryDetailContent.module.css';

type GlossaryDetailContentProps = {
  termCategory: string | null;
  termFqn: string | null;
  currentTab: string;
  breadcrumbItems?: BreadcrumbItemType[];
  onTermCreated?: (parentFqn: string) => void;
};

const Main = ({ termCategory, termFqn, currentTab, breadcrumbItems, onTermCreated }: GlossaryDetailContentProps) => {
  const navigate = useNavigate();
  const { data: categoryData } = useGlossaryTermCategoryInfo({
    category: termCategory || '',
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddTermModalOpen, setIsAddTermModalOpen] = useState(false);
  const [isTermAddModalOpen, setIsTermAddModalOpen] = useState(false);
  const [deleteCategoryConfirmOpen, setDeleteCategoryConfirmOpen] = useState(false);
  const [deleteTermConfirmOpen, setDeleteTermConfirmOpen] = useState(false);
  const [isTermEditModalOpen, setIsTermEditModalOpen] = useState(false);
  const [isTermDeleteStarted, setIsTermDeleteStarted] = useState(false);
  const { handleDelete: handleDeleteCategory, isPending: isDeletingCategory } = useDeleteGlossaryCategory();

  const { hasAnyRole } = useAuthorization();
  const canManageCategories = hasAnyRole(['admin', 'manager']);

  const iconConfig = useMemo(() => {
    const { iconColor } = getAssetIconStyle('glossaryTerm');
    return {
      component: <BookOutlined className="icon" style={{ color: iconColor }} />,
      wrapperStyle: getAssetIconWrapperStyle('glossaryTerm'),
      tooltip: getAssetTypeName('glossaryTerm'),
    };
  }, []);

  const fullTermFqn = useMemo(() => {
    if (!termFqn || !termCategory) return null;
    return `${termCategory}.${termFqn}`;
  }, [termCategory, termFqn]);

  const canAddSubTerm = useMemo(() => {
    if (!fullTermFqn) return true;
    const currentDepth = calculateFqnDepth(fullTermFqn);
    return currentDepth < TREE_DEPTH.GLOSSARY_TERM;
  }, [fullTermFqn]);

  const { handleDelete: handleDeleteTerm, isPending: isDeletingTerm } = useDeleteGlossaryTerm(
    fullTermFqn || undefined,
    termCategory || undefined,
    { onTreeRefresh: onTermCreated }
  );

  useEffect(() => {
    setIsTermDeleteStarted(false);
  }, [fullTermFqn]);

  const { data: termData, isLoading: isTermLoading } = useGlossaryTermDetail(
    fullTermFqn || '',
    !!fullTermFqn && !isTermDeleteStarted
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      if (!termCategory || !termFqn) return;
      navigate(`/glossary/${termCategory}/${termFqn}/${tab}`);
    },
    [navigate, termCategory, termFqn]
  );

  const handleOpenEditModal = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const handleOpenAddTermModal = useCallback(() => {
    setIsAddTermModalOpen(true);
  }, []);

  const handleCloseAddTermModal = useCallback(() => {
    setIsAddTermModalOpen(false);
  }, []);

  const handleOpenTermAddModal = useCallback(() => {
    setIsTermAddModalOpen(true);
  }, []);

  const handleCloseTermAddModal = useCallback(() => {
    setIsTermAddModalOpen(false);
  }, []);

  const handleOpenTermEditModal = useCallback(() => {
    setIsTermEditModalOpen(true);
  }, []);

  const handleCloseTermEditModal = useCallback(() => {
    setIsTermEditModalOpen(false);
  }, []);

  const handleDeleteCategoryConfirm = useCallback(() => {
    if (!categoryData?.id) return;
    setDeleteCategoryConfirmOpen(true);
  }, [categoryData]);

  const handleDeleteCategoryExecute = useCallback(async () => {
    if (!categoryData?.id) return;
    await handleDeleteCategory(categoryData.id);
  }, [categoryData, handleDeleteCategory]);

  const deleteCategoryMessage = useMemo(() => {
    if (!categoryData) return '';
    const categoryName = categoryData.displayName ?? categoryData.name;
    const termCount = categoryData.termCount ?? 0;
    const hasChildTerms = termCount > 0;
    return hasChildTerms
      ? `"${categoryName}" 용어집을 삭제하시겠습니까?\n하위 용어도 함께 삭제됩니다.`
      : `"${categoryName}" 용어집을 삭제하시겠습니까?`;
  }, [categoryData]);

  const handleDeleteTermConfirm = useCallback(() => {
    if (!termData?.id) return;
    setDeleteTermConfirmOpen(true);
  }, [termData]);

  const handleDeleteTermExecute = useCallback(async () => {
    if (!termData?.id) return;
    setIsTermDeleteStarted(true);
    await handleDeleteTerm(termData.id);
  }, [termData, handleDeleteTerm]);

  const deleteTermMessage = useMemo(() => {
    if (!termData) return '';
    const termName = termData.displayName ?? termData.name;
    const termCount = termData.childrenCount ?? 0;
    const hasChildTerms = termCount > 0;
    return hasChildTerms
      ? `"${termName}" 용어를 삭제하시겠습니까?\n하위 용어도 함께 삭제됩니다.`
      : `"${termName}" 용어를 삭제하시겠습니까?`;
  }, [termData]);

  const categoryMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'edit',
        label: '수정',
        icon: <Icon name="edit" size={16} />,
        onClick: handleOpenEditModal,
      },
      {
        key: 'delete',
        label: '삭제',
        icon: <Icon name="trash" width={13.6} />,
        onClick: handleDeleteCategoryConfirm,
        disabled: isDeletingCategory,
      },
    ],
    [handleOpenEditModal, handleDeleteCategoryConfirm, isDeletingCategory]
  );

  const termMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'edit',
        label: '수정',
        icon: <EditOutlined className={styles.icon} />,
        onClick: handleOpenTermEditModal,
      },
      {
        key: 'delete',
        label: '삭제',
        icon: <DeleteOutlined className={styles.icon} />,
        onClick: handleDeleteTermConfirm,
        disabled: isDeletingTerm,
      },
    ],
    [handleOpenTermEditModal, handleDeleteTermConfirm, isDeletingTerm]
  );

  const categoryDisplayName = categoryData?.displayName || categoryData?.name || '';

  if (!termCategory) {
    return null;
  }

  if (termFqn && fullTermFqn) {
    if (isTermLoading) {
      return (
        <div className={styles.contentWrapper}>
          <LoadingState message="용어 정보를 불러오는 중..." />
        </div>
      );
    }

    if (!termData) {
      return (
        <div className={styles.contentWrapper}>
          <NoDataBoundary data={null} emptyDescription={EMPTY_MESSAGES.NO_GLOSSARY}>
            <div />
          </NoDataBoundary>
        </div>
      );
    }

    return (
      <div className={styles.contentWrapper}>
        <ContentHeader
          breadcrumbItems={breadcrumbItems}
          icon={iconConfig}
          title={termData.displayName || termData.name}
          description={termData.description}
          actions={
            <>
              {canAddSubTerm && canManageCategories && (
                <Button prefix={<Icon name="add" size={16} />} onClick={handleOpenTermAddModal}>
                  하위 용어 추가
                </Button>
              )}
              {canManageCategories && (
                <Dropdown menu={{ items: termMenuItems }} trigger={['click']} rootClassName={styles.dropdown}>
                  <button type="button" className={styles.moreButton}>
                    <MoreOutlined size={16} color={COLOR.GRAY[10]} />
                  </button>
                </Dropdown>
              )}
            </>
          }
        />

        <GlossaryTermSummaryCard termData={termData} fullTermFqn={fullTermFqn} termCategory={termCategory} />

        <div className={styles.mainContent}>
          <div className={styles.termContentArea}>
            <GlossaryTermContent termFqn={fullTermFqn} currentTab={currentTab} onTabChange={handleTabChange} />
          </div>
        </div>

        {termData && categoryData && (
          <GlossaryTermFormModal
            open={isTermAddModalOpen}
            parentInfo={{
              glossaryId: categoryData.id,
              glossaryName: categoryData.name,
              parentId: termData.id,
              parentName: termData.displayName || termData.name,
              parentFqn: fullTermFqn,
            }}
            onClose={handleCloseTermAddModal}
            onSuccess={onTermCreated}
          />
        )}

        {termData && (
          <GlossaryTermFormModal
            open={isTermEditModalOpen}
            mode="edit"
            termData={termData}
            termFqn={fullTermFqn}
            onClose={handleCloseTermEditModal}
            onSuccess={onTermCreated}
          />
        )}

        <ConfirmModal
          open={deleteTermConfirmOpen}
          message={deleteTermMessage}
          confirmText="삭제"
          cancelText="취소"
          confirmButtonProps={{ danger: true }}
          onConfirm={handleDeleteTermExecute}
          onCancel={() => setDeleteTermConfirmOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className={styles.contentWrapper}>
      <NoDataBoundary data={categoryData} emptyDescription={EMPTY_MESSAGES.NO_CATEGORY}>
        <ContentHeader
          breadcrumbItems={breadcrumbItems}
          icon={iconConfig}
          title={categoryDisplayName}
          description={categoryData?.description}
          actions={
            canManageCategories && (
              <>
                <Button prefix={<Icon name="add" size={16} />} onClick={handleOpenAddTermModal}>
                  하위 용어 추가
                </Button>
                <Dropdown menu={{ items: categoryMenuItems }} trigger={['click']} rootClassName={styles.dropdown}>
                  <button type="button" className={styles.moreButton}>
                    <MoreOutlined size={16} color={COLOR.GRAY[10]} />
                  </button>
                </Dropdown>
              </>
            )
          }
        />

        <div className={styles.mainContent}>
          <div className={styles.contentArea}>
            <GlossaryTable />
          </div>
          <div className={styles.sidebarArea}>{categoryData && <GlossaryMetadataSidebar />}</div>
        </div>

        <GlossaryCategoryFormModal
          open={isEditModalOpen}
          mode="edit"
          categoryData={categoryData}
          onClose={handleCloseEditModal}
        />

        {categoryData && (
          <GlossaryTermFormModal
            open={isAddTermModalOpen}
            parentInfo={{
              glossaryId: categoryData.id,
              glossaryName: categoryData.name,
            }}
            onClose={handleCloseAddTermModal}
            onSuccess={onTermCreated}
          />
        )}

        <ConfirmModal
          open={deleteCategoryConfirmOpen}
          message={deleteCategoryMessage}
          confirmText="삭제"
          cancelText="취소"
          confirmButtonProps={{ danger: true }}
          onConfirm={handleDeleteCategoryExecute}
          onCancel={() => setDeleteCategoryConfirmOpen(false)}
        />
      </NoDataBoundary>
    </div>
  );
};

const Suspense = () => {
  return <SuspenseFallback />;
};

const Error = (props: React.ComponentProps<typeof ErrorFallback>) => {
  return (
    <div className={styles.error}>
      <ErrorFallback
        {...props}
        title={NOTIFICATION_MESSAGES.GLOSSARY_DETAIL_COMPONENT_ERROR}
        description={NOTIFICATION_MESSAGES.GLOSSARY_DETAIL_COMPONENT_ERROR_DESC}
      />
    </div>
  );
};

export const GlossaryDetailContent = {
  Main,
  Error,
  Suspense,
};
